import { gql } from 'graphql-request'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Info, Lightbulb } from 'lucide-react'

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@components/ui/dialog'
import { Input } from '@components/ui/input'
import { Button } from '@components/ui/button'
import { Switch } from '@components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip'
import { Checkbox } from '@components/ui/checkbox'
import { Label } from '@components/ui/label'

import Card from '@components/Card'
import Datepicker from '@components/Datepicker'
import { useMutation } from '@hooks/graphql'
import { useCurrentUser } from '@contexts/currentUser'

const CREATE_CLASSROOM_MUTATION = gql`
  mutation createClassroom($createClassroomInput: CreateClassroomInput!) {
    createClassroom(input: $createClassroomInput) {
      classroom {
        id
        name
        educator {
          id
          fullName
        }
      }
    }
  }
`

const NewClassroomModal = ({ isOpen, onClose }) => {
  const { currentMembership: { organization: { features = [] } = {} } = {} } = useCurrentUser()
  const researchClassroomsEnabled = features.includes('research_classrooms')
  const navigate = useNavigate()
  const { register, handleSubmit, formState: { errors }, control, watch, reset } = useForm({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      research: false,
      researchMotivationDocument: null,
      researchConsentingOnly: false,
      researchEthicalClearance: false,
      researchStartDate: null,
      researchEndDate: null
    }
  })

  const research = watch('research')

  const { mutateAsync: createClassroom, isLoading: isCreating } = useMutation({
    gqlMutation: CREATE_CLASSROOM_MUTATION,
    onSuccess: data => {
      const classroomId = data.createClassroom.classroom.id
      navigate(`/educators/classrooms/${classroomId}`)
      reset()
      onClose()
    }
  })

  const submit = data => {
    createClassroom({ createClassroomInput: data })
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>New classroom</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(submit)}
          className='flex flex-col space-y-6'
        >
          <div className='space-y-1'>
            <Label htmlFor='name'>Name</Label>
            <Input
              id='name'
              placeholder='e.g., Maths 101'
              {...register('name', { required: 'Name is required' })}
            />

            <If condition={errors.name}>
              <p className='text-sm text-destructive' role='alert'>{errors.name.message}</p>
            </If>
          </div>

          <If condition={researchClassroomsEnabled}>
            <div className='flex items-center space-x-2'>
              <Controller
                name='research'
                control={control}
                render={({ field }) => (
                  <Switch
                    id='is-research'
                    checked={field.value}
                    // eslint-disable-next-line react/jsx-handler-names
                    onCheckedChange={field.onChange}
                    aria-labelledby='research-label'
                  />
                )}
              />
              <Label htmlFor='is-research' id='research-label' className='flex items-center mb-0'>
                Research Classroom

                <Tooltip>
                  <TooltipTrigger>
                    <Info size={16} id='research-info-icon' />
                  </TooltipTrigger>
                  <TooltipContent className='max-w-[500px]'>
                    <p>Enable if this class will be part of a research study. Students will be asked to review and provide informed consent regarding the use of their data.</p>
                  </TooltipContent>
                </Tooltip>
              </Label>
            </div>

            <If condition={research}>
              <div className='space-y-4'>
                <div className='rounded-lg bg-muted p-4 flex items-start gap-3'>
                  <Lightbulb className='shrink-0' size={20} />
                  <p className='text-sm'>
                    Providing detailed study information helps ensure ethical compliance and clarity for participants.
                  </p>
                </div>

                <div>
                  <Label htmlFor='researchMotivationDocument'>
                    Research motivation document
                  </Label>
                  <Controller
                    name='researchMotivationDocument'
                    control={control}
                    rules={{ required: 'Research motivation is required' }}
                    render={({ field: { onChange, value, ...restField } }) => (
                      <Input
                        id='researchMotivationDocument'
                        type='file'
                        accept='.pdf,.doc,.docx'
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null
                          onChange(file)
                        }}
                        {...restField}
                      />
                    )}
                  />
                  <If condition={errors.researchMotivationDocument}>
                    <p className='text-sm mt-1 text-destructive' role='alert'>
                      {errors.researchMotivationDocument.message}
                    </p>
                  </If>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label>
                      Research start date
                    </Label>
                    <Controller
                      name='researchStartDate'
                      control={control}
                      rules={{ required: 'You must choose a start date' }}
                      // eslint-disable-next-line react/jsx-handler-names
                      render={({ field }) => <Datepicker {...field} />}
                    />
                    <If condition={errors.researchStartDate}>
                      <p className='text-sm mt-1 text-destructive' role='alert'>
                        {errors.researchStartDate.message}
                      </p>
                    </If>
                  </div>

                  <div>
                    <Label>
                      Research end date
                    </Label>
                    <Controller
                      name='researchEndDate'
                      control={control}
                      rules={{ required: 'You must choose an end date' }}
                      // eslint-disable-next-line react/jsx-handler-names
                      render={({ field }) => <Datepicker {...field} />}
                    />
                    <If condition={errors.researchEndDate}>
                      <p className='text-sm mt-1 text-destructive' role='alert'>
                        {errors.researchEndDate.message}
                      </p>
                    </If>
                  </div>
                </div>

                <Card className='flex flex-col items-start p-4 space-y-2'>
                  <div className='flex items-center gap-4'>
                    <Controller
                      name='researchEthicalClearance'
                      control={control}
                      rules={{ required: 'You must acknowledge this responsibility' }}
                      render={({ field }) => (
                        <Checkbox
                          id='research-ethical-clearance'
                          checked={field.value}
                          // eslint-disable-next-line react/jsx-handler-names
                          onCheckedChange={field.onChange}
                          aria-labelledby='ethical-clearance-label'
                        />
                      )}
                    />
                    <Label
                      className='leading-normal'
                      htmlFor='research-ethical-clearance'
                      id='ethical-clearance-label'
                    >
                      I acknowledge that I am responsible for getting the necessary ethical clearance from my institution to publish this research.
                    </Label>
                  </div>

                  <If condition={errors.researchEthicalClearance}>
                    <p className='text-sm text-destructive' role='alert'>{errors.researchEthicalClearance.message}</p>
                  </If>
                </Card>
              </div>
            </If>
          </If>
          <DialogFooter>
            <Button variant='outline' onClick={handleClose} type='button'>Cancel</Button>
            <Button
              disabled={isCreating}
              loading={isCreating}
              type='submit'
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default NewClassroomModal
